import React, { useEffect, useState } from 'react';
import Modal from 'global/components/modal';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { modalStyles } from './styles';
import { Text } from 'global/components/text';
import { Icon, MenuItem, Select, Checkbox, Input, Collapse, IconButton } from '@material-ui/core';
import { DROPOPTS } from '../manage/table';
import { get, flatten, uniqBy, size, debounce } from 'global/lib/lodash';
import clsx from 'clsx';
import SnackAlerts from 'global/components/snack-alerts';
import sendData from 'redux/actions/sendData';
import { getErrorMessage, _size } from 'global/utils';
import { useUser } from 'global/hooks/useUser';
import { awaitAll } from 'global/utils/awaitAll';
import Form from 'global/components/form';
import { Column, Row } from 'global/components/row';

export const CourseModal = (props) => {
  const { open } = props;
  const classes = modalStyles();
  const [selOpt, setSelOpt] = useState(0);
  const [scene, setScene] = useState(0);
  const [selected, setSelected] = useState({});
  const [selectedBundles, setSelectedBundles] = useState({});
  const [newTitle, setNewTitle] = useState('');
  const [loading, setLoading] = useState();
  const [alerts, setAlerts] = useState();
  const [formState, setFormState] = useState({});
  const user = useUser();

  const realm_id = get(user, 'realm.id', get(user, 'me.realm.id'));

  const bundles = useRetrieveData(
    { url: `/course-bundles` },
    {
      build: ['courseBundles'],
      params: {
        include: 'editions',
        filter: {
          realm_id,
        },
      },
    }
  );

  const users = useRetrieveData(
    { url: '/users' },
    {
      build: ['users'],
      params: {
        filter: {
          content_realm_id: realm_id,
        },
      },
      useCache: { reload: 1 },
    }
  );

  useEffect(() => {
    if (open) {
      setScene(props.startScene);
    }
  }, [open]);

  const getAllSelectedEditions = () => {
    const eds = Object.keys(selected).map((k) => get(selected, k));
    const bunds = flatten(Object.keys(selectedBundles).map((k) => get(selectedBundles, [k, 'editions'], [])));

    return uniqBy([...eds, ...bunds], 'id');
  };

  const editions = useRetrieveData(
    { url: `/editions` },
    {
      build: ['editions'],
      params: {
        filter: { edition_type: 'course' },
      },
    }
  );

  const onClose = (updated = false, created) => {
    setScene(0);
    setSelected({});
    setSelectedBundles({});
    setFormState({});
    setNewTitle('');
    setSelOpt(0);
    props.onClose && props.onClose(updated, created);

    if (updated) {
      editions.update();
      bundles.update();
    }
  };

  const putToFormat = (arr) => {
    return arr.map((e) => ({ id: e.id, type: 'editions' }));
  };

  const getSelectedUsers = () => {
    const formUsers = get(formState, 'users');
    if (formUsers) {
      const obj = {};
      formUsers.map((u) => {
        obj[u.id] = { ...u, reportable: { ...u } };
      });
      return obj;
    } else {
      return props.selected;
    }
  };

  const submit = async () => {
    try {
      setLoading(true);

      const selectedEditions = getAllSelectedEditions();

      if (!!newTitle) {
        //need to create bundle first
        await sendData({
          url: `/course-bundles`,
          method: 'post',
          data: {
            data: {
              type: 'course-bundles',
              attributes: {
                name: newTitle,
                realm_id,
              },
              relationships: {
                editions: {
                  data: putToFormat(selectedEditions),
                },
              },
            },
          },
        });
      }

      let error;

      const selUsers = getSelectedUsers();

      const created = await awaitAll(
        Object.keys(selUsers).map((k) => () => async () => {
          const user = selUsers[k];
          const user_id = get(user, 'reportable.id');
          const uAssignments = get(user, 'userAssignments', []) || [];
          let assignments = [];

          // 0 = add
          // 1 = remove
          // 2 = overwrite
          const selIndex = selectedEditions.map((e) => e.id);
          const userIndex = uAssignments.map((e) => get(e, 'editionId', e.id).toString());

          if (selOpt === 0) {
            //add
            assignments = selectedEditions.filter((e) => userIndex.indexOf(e.id) === -1).map((e) => ({ ...e, method: 'post' }));
          } else if (selOpt === 1) {
            //remove
            assignments = uAssignments.filter((e) => selIndex.indexOf(e.editionId.toString()) > -1).map((e) => ({ ...e, method: 'delete' }));
          } else if (selOpt === 2) {
            //overwrite
            assignments = uniqBy([...uAssignments, ...selectedEditions], 'id')
              .map((e) => {
                const isEdition = !e.editionId; //uAssignments comes with editionId inside, selectedEditions don't

                const existInSel = selIndex.indexOf(`${isEdition ? e.id : e.editionId}`) > -1;
                const existsInUser = userIndex.indexOf(`${isEdition ? e.id : e.editionId}`) > -1;

                const method = (() => {
                  if (existInSel && !existsInUser) {
                    return 'post'; //need to create new assignment
                  } else if (existsInUser && !existInSel) {
                    return 'delete'; //need to delete previous
                  }
                  //otherwise leave unchanged
                })();

                return {
                  ...e,
                  method,
                };
              })
              .sort((a, b) => (a.method === 'delete' ? -1 : b.method === 'delete' ? 1 : 0));
          }

          const createdAssignments = flatten(
            await awaitAll(
              assignments.map((e) => () => async () => {
                if (!e.method) {
                  return;
                }

                return await sendData(
                  {
                    url: `/course-assignments${e.method !== 'post' ? `/${e.id}` : ''}`,
                    method: e.method,
                    data: {
                      data: {
                        id: e.method !== 'post' ? e.id : undefined,
                        type: 'course-assignments',
                        attributes: {
                          user_id,
                          realm_id,
                          edition_id: e.id,
                        },
                      },
                    },
                  },
                  e.method === 'delete'
                    ? {}
                    : {
                        params: {
                          include: 'assessment',
                        },
                        normalize: { build: ['courseAssignments'] },
                      }
                ).catch((e) => (error = e));
              }),
              { queueSize: 1 }
            )
          ).filter((f) => f && f.id);

          return {
            createdAssignments,
            user,
          };
        }),
        { queueSize: 1 }
      );

      if (error) {
        throw error;
      }
      setAlerts({ message: `Courses ${selOpt === 0 ? 'added' : selOpt === 1 ? 'removed' : 'overwritten'} successfully!`, severity: 'success' });
      onClose(true, selOpt == 1 ? undefined : created);
    } catch (err) {
      setAlerts({ message: getErrorMessage(err), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const renderButtons = () => {
    const isCreate = scene === 1 || scene === -1;
    const isFormUsers = !!get(formState, 'users');
    return [
      { title: 'Back', disabled: scene === -1 || (scene == 0 && !isFormUsers), onClick: () => setScene((!newTitle && !isFormUsers) || (scene === 2 && isFormUsers) ? 0 : scene - 1) },
      { title: 'Next', disabled: scene === -1 ? !_size(get(formState, 'users')) : scene == 2 || (!size(getAllSelectedEditions()) && scene !== -1), onClick: () => setScene(!isCreate ? 2 : scene + 1) },
      { title: 'Submit', loading, disabled: scene !== 2, onClick: debounce(submit, 200) },
    ];
  };

  const toggleSelected = (edition) => {
    const sel = { ...selected };
    const isSelected = !!get(sel, edition.id);
    if (isSelected) {
      delete sel[edition.id];
    } else {
      sel[edition.id] = edition;
    }
    setSelected(sel);
  };

  const toggleSelectedBundles = (edition) => {
    const sel = { ...selectedBundles };
    const isSelected = !!get(sel, edition.id);
    if (isSelected) {
      delete sel[edition.id];
    } else {
      sel[edition.id] = edition;
    }
    setSelectedBundles(sel);
  };

  const getText = () => {
    const selDrop = selOpt;
    const selCheck = getAllSelectedEditions();

    const selCheckLength = selCheck.length;

    const keyword = selDrop === 0 ? 'Add' : selDrop === 1 ? 'Remove' : 'Overwrite';
    const courseTitle = `${keyword} ${selCheckLength > 1 ? 'these' : 'this'} course${selCheckLength > 1 ? 's' : ''}:`;

    const selUsers = getSelectedUsers();

    const userLength = Object.keys(selUsers).length;

    return {
      courseTitle,
      courses: selCheck.map((up) => up.name).join(', '),
      userTitle: `${selDrop === 0 ? 'To' : selDrop === 1 ? 'From' : 'On'} ${userLength > 1 ? 'these' : 'this'} ${userLength} user${userLength > 1 ? 's' : ''}:`,
      users: Object.keys(selUsers)
        .map((k) => `${get(selUsers, [k, 'reportable', 'name'])} - ${get(selUsers, [k, 'reportable', 'email'])}`)
        .join(', '),
    };
  };

  const createNew = () => {
    setScene(1);
  };

  const renderScene = () => {
    if (scene === -1) {
      return (
        <>
          <Form
            loading={users.loading}
            showAllScreens
            onFormState={setFormState}
            screens={[
              {
                title: '',
                inputs: [
                  {
                    id: 'users',
                    type: 'UserSelect',
                    data: users.data,
                    defaultValue: () => get(formState, 'users', []),
                    props: {
                      placeholder: 'Select Users',
                    },
                  },
                ],
              },
            ]}
          />
        </>
      );
    }

    if (scene === 0) {
      return (
        <>
          <div>
            <Select size="small" variant="outlined" value={selOpt} fullWidth onChange={(e) => setSelOpt(e.target.value)}>
              {DROPOPTS.map((opt, i) => (
                <MenuItem key={i} {...opt}>
                  {opt.title}
                </MenuItem>
              ))}
            </Select>
          </div>

          <div>
            <Text className={classes.title}>COURSE BUNDLES</Text>
            <div className={classes.grayBG}>
              {bundles.data.map((edition) => (
                <BundleEdition key={edition.id} edition={edition} selectedBundles={selectedBundles} toggleSelectedBundles={toggleSelectedBundles} />
              ))}

              <div className={clsx(classes.row, classes.selectable)} onClick={createNew}>
                <Icon className={classes.rowIcon}>add</Icon>
                <div>CREATE NEW BUNDLE</div>
              </div>
            </div>
          </div>

          <div>
            <Text className={classes.title}>INDIVIDUAL COURSES</Text>
            <div className={classes.grayBG}>
              {editions.data.map((edition) => {
                const isSelected = !!get(selected, edition.id);
                return (
                  <div className={classes.row}>
                    <Checkbox onClick={() => toggleSelected(edition)} checked={isSelected} />
                    <div>{edition.name}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      );
    }

    if (scene === 2) {
      //final submit scene
      const text = getText();
      const hasBundle = !!newTitle;
      return (
        <>
          {hasBundle && (
            <Text type="title_large" className={classes.sceneTitle}>
              {'Create Bundle:'}
            </Text>
          )}
          {hasBundle && <Text className={classes.sceneText}>{newTitle}</Text>}
          <Text type="title_large" className={classes.sceneTitle}>
            {text.courseTitle}
          </Text>
          <Text className={classes.sceneText}>{text.courses}</Text>
          <Text type="title_large" className={classes.sceneTitle}>
            {text.userTitle}
          </Text>
          <Text className={classes.sceneText}>{text.users}</Text>
        </>
      );
    }

    if (scene === 1) {
      //create new scene
      return (
        <>
          <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} fullWidth placeholder="Title" />

          <div>
            <Text className={classes.title}>COURSES</Text>
            <div className={classes.grayBG}>
              {editions.data.map((edition) => {
                const isSelected = !!get(selected, edition.id);
                return (
                  <div className={classes.row}>
                    <Checkbox onClick={() => toggleSelected(edition)} checked={isSelected} />
                    <div>{edition.name}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      );
    }
  };

  return (
    <>
      <Modal open={!!open} onClose={() => onClose()} title={'COURSE ASSIGNMENTS'} buttons={renderButtons()}>
        {renderScene()}
      </Modal>
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </>
  );
};

const BundleEdition = (props) => {
  const { selectedBundles, toggleSelectedBundles, edition } = props;
  const [open, setOpen] = useState(false);

  const isSelected = !!get(selectedBundles, edition.id);
  return (
    <Column style={{ alignItems: 'flex-start' }}>
      <Row fullWidth>
        <Checkbox onClick={() => toggleSelectedBundles(edition)} checked={isSelected} />
        <div>{edition.name}</div>
        <IconButton style={{ marginLeft: 'auto' }} onClick={() => setOpen(!open)}>
          <Icon>{open ? 'arrow_drop_up' : 'arrow_drop_down'}</Icon>
        </IconButton>
      </Row>
      <Collapse in={open}>
        <div style={{ padding: 8 }}>
          {get(edition, 'editions', []).map((e) => (
            <div>{get(e, 'name', 'N/A')}</div>
          ))}
        </div>
      </Collapse>
    </Column>
  );
};
